import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonical?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  articlePublishDate?: string;
  articleModifiedDate?: string;
  author?: string;
}

export function SEOHead({
  title,
  description,
  keywords = "53吃瓜,53吃瓜网,吃瓜52,53吃瓜圈,娱乐八卦,吃瓜52更新,明星爆料,圈内猛料",
  ogImage = "https://readdy.ai/api/search-image?query=modern%20news%20portal%20abstract%20background%2C%20dark%20elegant%20design%2C%20subtle%20red%20and%20orange%20accent%20lights%2C%20minimalist%20geometric%20shapes%2C%20premium%20quality%20feel&width=1200&height=630&seq=og-default&orientation=landscape",
  ogType = "website",
  canonical,
  jsonLd,
  articlePublishDate,
  articleModifiedDate,
  author,
}: SEOHeadProps) {
  const location = useLocation();
  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const canonicalUrl = canonical || currentUrl;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const setProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("property", property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setMeta("description", description);
    setMeta("keywords", keywords);
    setMeta("author", author || "53吃瓜");
    setMeta("robots", "index, follow");

    setProperty("og:type", ogType);
    setProperty("og:title", title);
    setProperty("og:description", description);
    setProperty("og:url", currentUrl);
    setProperty("og:image", ogImage);
    setProperty("og:site_name", "53吃瓜");

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", ogImage);

    // Canonical
    let linkCanonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.rel = "canonical";
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.href = canonicalUrl;

    // Article meta
    if (articlePublishDate) {
      let metaPublish = document.querySelector('meta[property="article:published_time"]') as HTMLMetaElement | null;
      if (!metaPublish) {
        metaPublish = document.createElement("meta");
        metaPublish.setAttribute("property", "article:published_time");
        document.head.appendChild(metaPublish);
      }
      metaPublish.content = articlePublishDate;
    }
    if (articleModifiedDate) {
      let metaMod = document.querySelector('meta[property="article:modified_time"]') as HTMLMetaElement | null;
      if (!metaMod) {
        metaMod = document.createElement("meta");
        metaMod.setAttribute("property", "article:modified_time");
        document.head.appendChild(metaMod);
      }
      metaMod.content = articleModifiedDate;
    }
    if (author) {
      let metaAuthor = document.querySelector('meta[property="article:author"]') as HTMLMetaElement | null;
      if (!metaAuthor) {
        metaAuthor = document.createElement("meta");
        metaAuthor.setAttribute("property", "article:author");
        document.head.appendChild(metaAuthor);
      }
      metaAuthor.content = author;
    }

    // JSON-LD
    if (jsonLd) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
      existingScripts.forEach((s) => s.remove());

      const scripts = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      scripts.forEach((data) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      });
    }

    return () => {
      if (jsonLd) {
        const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
        existingScripts.forEach((s) => s.remove());
      }
    };
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, currentUrl, jsonLd, articlePublishDate, articleModifiedDate, author]);

  return null;
}